import { useState } from "react";
import api from "../lib/api";
import { Button } from "./ui/button";
import { Switch } from "./ui/switch";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "./ui/dialog";
import { Share2, Copy, Check, Globe, Lock } from "lucide-react";
import { toast } from "sonner";

export default function LibraryShareDialog({ open, onOpenChange, library, onUpdate }) {
  const [copied, setCopied] = useState(false);
  const shareUrl = library?.share_id
    ? `${window.location.origin}/share/library/${library.share_id}`
    : "";

  const togglePublic = async (next) => {
    if (!library?.id) return;
    try {
      const { data } = await api.post(`/caption-libraries/${library.id}/share`, { is_public: next });
      onUpdate?.({
        ...library,
        is_public: data.is_public,
        share_id: data.share_id,
      });
      toast.success(next ? "Public sharing enabled" : "Sharing disabled");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Couldn't copy");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-50">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <Share2 className="w-4 h-4" /> Share library
          </DialogTitle>
          <DialogDescription className="text-zinc-400">
            Generate a public link so others can preview and import this library into their own account.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5">
          <div className="flex items-center justify-between bg-zinc-950 border border-zinc-800 rounded-md p-4">
            <div className="flex items-center gap-3">
              {library?.is_public
                ? <Globe className="w-5 h-5 text-emerald-400" />
                : <Lock className="w-5 h-5 text-zinc-500" />}
              <div>
                <div className="font-medium">
                  {library?.is_public ? "Anyone with the link" : "Only you"}
                </div>
                <div className="text-xs text-zinc-400">
                  {library?.is_public
                    ? "Read-only library page. Signed-in viewers can copy it into their own libraries."
                    : "Toggle on to generate a sharable link."}
                </div>
              </div>
            </div>
            <Switch
              data-testid="library-share-toggle"
              checked={!!library?.is_public}
              onCheckedChange={togglePublic}
            />
          </div>

          {library?.is_public && (
            <div className="space-y-2">
              <Label className="label-eyebrow">Public URL</Label>
              <div className="flex items-center gap-2">
                <Input
                  data-testid="library-share-url"
                  readOnly
                  value={shareUrl}
                  className="bg-zinc-950 border-zinc-800 font-mono text-xs"
                />
                <Button
                  data-testid="library-share-copy"
                  type="button"
                  onClick={copy}
                  className="bg-blue-600 hover:bg-blue-500 shrink-0"
                >
                  {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                </Button>
              </div>
              <p className="text-xs text-zinc-500">
                Anyone with this link can preview the templates. Signed-in users can “Add to my libraries”.
              </p>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
